/**
 * DAG Sync React Hooks
 * 
 * Provides React hooks for using the DAG sync system with automatic cleanup
 * and state management
 */

import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useIslandBranch } from '../active-branch';
import { useAtomValue } from 'jotai';
import { DAGSyncClient, DAGSyncEventHandler } from './client';
import { dumpIsland } from './store-dump';
import { PartDeltaHandler } from '../part-streaming/client';
import {
  DAGSubscription,
  DAGContext,
  DAGNode,
  DAGRelationship,
  LoadingState,
} from './types';
import { PartDeltaEvent } from '../part-streaming/types';
import { dagSyncSocketAtom, isDagSyncConnectedAtom } from '@sourcepilot/ui/framework/data/hooks/use-dag-sync-socket';
import { useReactiveNode } from '../hooks/use-reactive-node';
import type { NeoNodeBase } from '@sourcepilot/framework/neo-node-base';

/** The parity harness's browser hook (VC-e2e): `window.__spStoreDump(island)`
 *  answers what this page SHOWS for the island, in the canonical form. Only
 *  in a DEV_AUTH build; a production build never has it. */
function installStoreDumpHook(client: DAGSyncClient): void {
  if (process.env.NEXT_PUBLIC_DEV_AUTH !== 'true' || typeof window === 'undefined') return;
  (window as any).__spStoreDump = (islandId: string) => dumpIsland(client, islandId);
}

// Global DAG sync client instances - keyed by socket or URL
let dagSyncClient: DAGSyncClient | null = null;
let existingSocketClient: DAGSyncClient | null = null;
let currentSocketRef: WebSocket | null = null;

export function getDAGSyncClient(existingSocket?: WebSocket | null): DAGSyncClient {
  if (existingSocket) {
    // Use singleton pattern for existing socket too to prevent multiple clients
    if (!existingSocketClient || currentSocketRef !== existingSocket) {
      // a NEW socket is a reconnect: the new client takes over what the old
      // one held, so its first re-loads remove what was deleted meanwhile
      const previous = existingSocketClient;
      existingSocketClient = new DAGSyncClient('', existingSocket);
      if (previous) existingSocketClient.inheritFrom(previous);
      currentSocketRef = existingSocket;
      installStoreDumpHook(existingSocketClient);
    }
    return existingSocketClient;
  } else {
    // ONE client per page. A caller that has no socket in hand JOINS the
    // client already holding the app's shared socket (Shell opens it) rather
    // than opening a second connection of its own. Two clients meant two
    // `dag_sync_socket` connections, two subscription maps and two graph
    // stores, so a patch could land on a store the view does not render
    // from, and one client's teardown could cut the other's delivery.
    if (existingSocketClient) {
      return existingSocketClient;
    }
    // Use singleton for new connections
    if (!dagSyncClient) {
      const wsUrl = process.env.NEXT_PUBLIC_SOURCEPILOT_WS_URL;
      if (!wsUrl) {
        throw new Error('NEXT_PUBLIC_SOURCEPILOT_WS_URL environment variable is required');
      }
      dagSyncClient = new DAGSyncClient(`${wsUrl}/dag_sync_socket`);
    }
    return dagSyncClient;
  }
}

// Hook for DAG synchronization
export function useDAGSync(context: DAGContext | null) {
  const [subscription, setSubscription] = useState<DAGSubscription | null>(null);
  const [loadingState, setLoadingState] = useState<LoadingState>('loading');
  const [error, setError] = useState<string | null>(null);

  // Read socket state directly from Jotai atoms (don't call useDagSyncSocket hook)
  // This prevents unnecessary hook instance warnings
  // Note: These atoms use the default Provider store, not graphStoreJotaiStore
  const socket = useAtomValue(dagSyncSocketAtom);
  const isConnected = useAtomValue(isDagSyncConnectedAtom);

  // Always use the Jotai WebSocket - wait for it to be ready
  const client = useMemo(() => {
    // Don't create any client until we have a connected socket from Jotai
    if (socket && isConnected) {
      return getDAGSyncClient(socket);
    }
    return null;
  }, [socket, isConnected]);

  // Stable context references using refs to prevent infinite loops
  const contextRef = useRef(context);

  // Update refs when props change
  contextRef.current = context;

  // Phase C: the island's bound branch is part of the subscription identity —
  // a branch switch re-syncs (new snapshot through the branch lens, patches
  // filtered to that branch)
  const islandBranch = useIslandBranch(context?.context_id);
  // Create stable context key for effect dependency
  const contextKey = useMemo(() =>
    context ? `${context.context.label}:${context.context_id}@${islandBranch}` : null,
    [context?.context.label, context?.context_id, islandBranch]
  );

  // 404-on-sync usually means the context is being created RIGHT NOW
  // (navigate-on-create races the backend write — chat/new, create_project).
  // Retry with backoff while showing the loading state; only surface the
  // error once the entity is genuinely absent. ~12s total before giving up.
  const RETRY_MAX_ATTEMPTS = 4;
  const retryAttemptsRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);

  // New context → fresh retry budget.
  useEffect(() => {
    retryAttemptsRef.current = 0;
  }, [contextKey]);

  const handleUpdate: DAGSyncEventHandler = useCallback((updatedSubscription) => {
    if (
      updatedSubscription.errorCode === 404 &&
      retryAttemptsRef.current < RETRY_MAX_ATTEMPTS
    ) {
      // Keep the view in its loading state and schedule a re-sync — the
      // effect below re-runs on retryNonce and re-sends sync-dag (the
      // client never reuses an errored subscription).
      const attempt = retryAttemptsRef.current++;
      setLoadingState('loading');
      setError(null);
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      retryTimerRef.current = setTimeout(
        () => setRetryNonce((n) => n + 1),
        800 * 2 ** attempt,
      );
      return;
    }
    if (!updatedSubscription.error && !updatedSubscription.isLoading) {
      // Reset only on a completed healthy sync — each retry's re-sync
      // emits an errorless LOADING update first, and resetting on that
      // would refill the budget every cycle (infinite retry loop).
      retryAttemptsRef.current = 0;
    }
    setSubscription({ ...updatedSubscription }); // Create new object to trigger re-renders
    // a refused or failed subscription is an ERROR state, not a loaded page
    // with nothing in it: a page's error view (DAGLayout) shows why only
    // then (VC-e2e: a refused project rendered its shell empty)
    setLoadingState(updatedSubscription.error ? 'error' : updatedSubscription.isLoading ? 'loading' : 'loaded');
    setError(updatedSubscription.error || null);
  }, []);

  const handleError = useCallback((errorMessage: string) => {
    setError(errorMessage);
    setLoadingState('error');
  }, []);

  // NOTE: Connection is managed by layout.tsx
  // We don't call connect() here to avoid duplicate connection attempts
  // when multiple components use useDAGSync
  // The layout.tsx handles the single global connection

  useEffect(() => {
    // Don't sync if no context provided (e.g., Chat component with no threadId)
    if (!contextRef.current || !context) {
      setLoadingState('loaded'); // Not loading if no context
      return;
    }

    // Don't sync if no client yet
    if (!client) {
      // Wait for client to be ready
      setLoadingState('loaded'); // Not loading if no context
      return;
    }

    // Don't sync if not connected (wait for connection)
    if (!isConnected) {
      return;
    }

    let subscriptionId: string;
    // THIS effect's context: the cleanup releases what this run synced. The
    // ref already holds the NEXT render's context when the cleanup runs (a
    // layout whose id changed, P1 → P2), and unsyncing that left P1 held.
    const syncedContext = contextRef.current;
    let cleanedUp = false;

    const initializeSync = async () => {
      try {
        setLoadingState('loading');
        setError(null);

        // Register the error handler BEFORE sending sync-dag. Errors
        // (e.g. backend's 404 response when context_id doesn't exist)
        // come back fast — if we wait until after `await syncDAG`
        // returns, a quick error response can land in handleMessage
        // before this hook's handler is in the registry, and the
        // error is silently dropped. Error handlers are a global Set
        // (notifyErrorHandlers iterates all), not subscription-keyed,
        // so registering early is safe — we just remove this exact
        // handler reference on cleanup.
        client.onError(handleError);

        const sub = await client.syncDAG(syncedContext);
        if (cleanedUp) {
          // left before the sync answered: give the hold straight back
          client.unsyncDAG(syncedContext);
          return;
        }
        subscriptionId = sub.subscription_id;

        // Update handler is per-subscription, so it must come after
        // syncDAG returns with the subscription ID.
        client.onDAGUpdate(subscriptionId, handleUpdate);

      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
        console.error(`DAG sync failed:`, errorMessage);
        setError(errorMessage);
        setLoadingState('error');
      }
    };

    initializeSync();

    return () => {
      // Cleanup on unmount or reconnect
      cleanedUp = true;
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      if (subscriptionId && client) {
        // Remove this component's event handler
        client.removeDAGUpdateHandler(subscriptionId, handleUpdate);
        // Decrement ref count and potentially unsync
        client.unsyncDAG(syncedContext);
      }
      if (client) {
        client.removeErrorHandler(handleError);
      }
    };
  }, [client, contextKey, isConnected, handleUpdate, handleError, retryNonce]); // Added isConnected!

  // Convenience methods
  const getNodes = useCallback((): DAGNode[] => {
    return subscription ? Array.from(subscription.nodes.values()) : [];
  }, [subscription]);

  const getNode = useCallback((nodeId: string): DAGNode | undefined => {
    return subscription?.nodes.get(nodeId);
  }, [subscription]);

  const getRelationships = useCallback((): DAGRelationship[] => {
    return subscription ? Array.from(subscription.relationships.values()) : [];
  }, [subscription]);

  const getRelationshipsForNode = useCallback((nodeId: string): DAGRelationship[] => {
    const relationships = getRelationships();
    return relationships.filter(rel => rel.source === nodeId || rel.target === nodeId);
  }, [getRelationships]);

  // Memoize the arrays to prevent unnecessary re-renders
  // Include version to ensure updates are detected during streaming
  const nodes = useMemo(() => getNodes(), [subscription, subscription?.version]);
  const relationships = useMemo(() => getRelationships(), [subscription, subscription?.version]);

  // Get reactive root node (for contexts like thread, workspace, etc.)
  // Subscribes to GraphStore so updates from DAG patches trigger re-renders
  const rootNode = useReactiveNode<NeoNodeBase<any, any>>(context?.context_id ?? null);

  return {
    subscription,
    nodes,
    relationships,
    loadingState,
    error,
    isLoading: loadingState === 'loading',
    isError: loadingState === 'error',
    isLoaded: loadingState === 'loaded',
    getNode,
    getNodes,
    getRelationships,
    getRelationshipsForNode,
    rootNode,  // Reactive root node instance (e.g., Thread, Product, Workspace)
  };
}


// Hook for task-level delta streaming (masterplan/28 §4): mount ONCE per
// open chat (ChatView / SpawnedTaskDisplay). Subscribes to the task's
// delta stream; part renderers then register handlers via
// usePartDeltaStreaming with no per-part round-trips. Reconnects resume
// from the last stream id seen.
export function useTaskDeltaStream(taskId: string | null) {
  const socket = useAtomValue(dagSyncSocketAtom);
  const isConnected = useAtomValue(isDagSyncConnectedAtom);
  const resumeCursorRef = useRef<string>("0");

  const client = useMemo(() => {
    if (socket && isConnected) {
      return getDAGSyncClient(socket);
    }
    return null;
  }, [socket, isConnected]);

  useEffect(() => {
    if (!taskId || !client) return;

    let cancelled = false;
    const subscribe = async () => {
      try {
        await client.connect();
        if (cancelled) return;
        if (client.partStreamingClient) {
          client.partStreamingClient.subscribeToTaskDeltas(
            taskId,
            resumeCursorRef.current
          );
        } else {
          console.error('useTaskDeltaStream: no partStreamingClient available');
        }
      } catch (error) {
        console.error('Failed to subscribe to task deltas:', error);
      }
    };
    subscribe();

    return () => {
      cancelled = true;
      if (client?.partStreamingClient) {
        // Remember where we got to so a remount/reconnect resumes
        // instead of replaying the retained stream from the start.
        resumeCursorRef.current =
          client.partStreamingClient.getTaskResumeCursor(taskId);
        client.partStreamingClient.unsubscribeFromTaskDeltas(taskId);
      }
    };
  }, [taskId, client]);
}

// Hook for part delta streaming (thread-specific)
export function usePartDeltaStreaming(
  partId: string | null,
  startSequence: number = 0,
  onDelta?: (delta: PartDeltaEvent) => void
) {
  const [deltaCount, setDeltaCount] = useState(0);

  // Read socket state directly from Jotai atoms (don't call useDagSyncSocket hook)
  // This prevents unnecessary hook instance warnings
  // Note: These atoms use the default Provider store, not graphStoreJotaiStore
  const socket = useAtomValue(dagSyncSocketAtom);
  const isConnected = useAtomValue(isDagSyncConnectedAtom);

  // Get client with shared WebSocket
  const client = useMemo(() => {
    if (socket && isConnected) {
      return getDAGSyncClient(socket);
    }
    return null;
  }, [socket, isConnected]);

  const handlePartDelta: PartDeltaHandler = useCallback((event: PartDeltaEvent) => {
    setDeltaCount(prev => prev + 1);
    
    // Call the callback immediately when delta arrives
    if (onDelta) {
      onDelta(event);
    }
  }, [onDelta]);

  useEffect(() => {    
    if (!partId || !client) {
      setDeltaCount(0);
      return;
    }

    const subscribeToDeltas = async () => {
      try {
        // Wait for client connection and part streaming client initialization
        await client.connect();
        if (client.partStreamingClient) {
          await client.partStreamingClient.subscribeToPartDeltas(partId, handlePartDelta, startSequence);
        } else {
          console.error('usePartDeltaStreaming: no partStreamingClient available');
        }
      } catch (error) {
        console.error('Failed to subscribe to part deltas:', error);
      }
    };

    subscribeToDeltas();

    return () => {
      if (partId && client?.partStreamingClient) {
        client.partStreamingClient.unsubscribeFromPartDeltas(partId);
      }
    };
  }, [partId, startSequence, client, handlePartDelta]);

  const clearDeltas = useCallback(() => {
    setDeltaCount(0);
  }, []);

  return {
    deltaCount,
    clearDeltas,
  };
}
